import { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import type { MeetingAccess } from "../../domain/meetingAccess";

export type { MeetingAccess } from "../../domain/meetingAccess";

export const MeetingsAPI = {
    /** The therapist's own token for a booking they own. */
    therapistToken: (
        appointmentId: string
    ): Promise<AxiosResponse<MeetingAccess>> =>
        apiClient.post(`/scheduler/meetings/${appointmentId}/token`),

    /**
     * The session a booking is held as, created the first time it is asked
     * for. A booking has no session until the therapist actually turns up:
     * this is what gives them notes to record into, a dock, and something to
     * finish — the things a bare room has none of.
     */
    sessionForAppointment: (
        appointmentId: string
    ): Promise<AxiosResponse<{ session_id: string }>> =>
        apiClient.post(`/scheduler/meetings/${appointmentId}/session`),

    /** A therapist's token for a session started on the spot, with no booking. */
    therapistSessionToken: (
        sessionId: string
    ): Promise<AxiosResponse<MeetingAccess>> =>
        apiClient.post(`/sessions/${sessionId}/meeting-token`),

    /**
     * A guest joining from the link itself — no account, no portal. The token
     * in the URL is the authorisation.
     */
    guestToken: (
        token: string,
        displayName: string
    ): Promise<AxiosResponse<MeetingAccess>> =>
        apiClient.post(`/public/meetings/${token}/token`, {
            display_name: displayName,
        }),

    /**
     * Say that the room is up, which is the first moment it can be recorded:
     * LiveKit creates a room when someone joins it, so until the therapist is
     * connected there is nothing there to record.
     */
    startSessionMeeting: (
        sessionId: string
    ): Promise<AxiosResponse<{ recording: boolean }>> =>
        apiClient.post(`/sessions/${sessionId}/meeting/started`),

    /**
     * Close a session's room for everyone in it.
     *
     * The therapist finishing is the session ending, not one person leaving:
     * without this the patient is left alone in a room, unable to tell whether
     * it is over or the connection dropped. The server stops the recording and
     * settles the session's status from what actually happened.
     */
    endSessionMeeting: (
        sessionId: string
    ): Promise<AxiosResponse<{ recording: boolean }>> =>
        apiClient.post(`/sessions/${sessionId}/meeting/end`),

    /**
     * The therapist's own "not this one", for a single session.
     *
     * Turning it off stops the recording that is running and deletes what it
     * captured; turning it back on starts one again if the room is up and the
     * rules that always applied still allow it. The answer carries both where
     * the switch now is and whether anything is actually recording — they are
     * different questions, and only the second decides whether notes come.
     */
    setSessionTranscription: (
        sessionId: string,
        enabled: boolean
    ): Promise<
        AxiosResponse<{ transcription_enabled: boolean; recording: boolean }>
    > =>
        apiClient.put(`/sessions/${sessionId}/meeting/transcription`, {
            enabled,
        }),
};
